import React, { useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Search, Calendar, Download, X, ChevronDown, FileText, FileSpreadsheet, Flag } from "lucide-react";
import { format } from "date-fns";
import html2pdf from "html2pdf.js";

// User-entered values go into an HTML string (PDF export), so escape them.
const escapeHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

// Spreadsheet apps run cells starting with = + - @ as formulas; prefix them.
const csvSafe = (value) => {
  const text = String(value ?? "");
  return /^[=+\-@\t\r]/.test(text) ? `'${text}` : text;
};

export default function WithdrawalSearchFilter({
  searchQuery,
  onSearch,
  dateRange,
  onDateRangeChange,
  onClearFilters,
  withdrawals,
  currentStatus,
  flaggedOnly,
  onFlaggedOnlyChange
}) {
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [localSearch, setLocalSearch] = useState(searchQuery);

  const handleSearchChange = (e) => {
    setLocalSearch(e.target.value);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    onSearch(localSearch);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      onSearch(localSearch);
    }
  };

  const handleDateChange = (dates) => {
    const [start, end] = dates;
    onDateRangeChange({ startDate: start, endDate: end });
    if (start && end) {
      setShowDatePicker(false);
    }
  };

  const hasActiveFilters = searchQuery || dateRange.startDate || dateRange.endDate || flaggedOnly;

  const formatAmount = (amount) => {
    return amount.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  const exportToCSV = () => {
    if (!withdrawals || withdrawals.length === 0) {
      alert("No data to export");
      return;
    }

    const headers = [
      "Reference No",
      "Request From",
      "Receiver Name",
      "Mobile No",
      "Amount",
      "Status",
      "Withdrawal Type",
      "Flagged",
      "Date",
      "Note",
      "Approved/Rejected Notes"
    ];

    const rows = withdrawals.map(w => [
      w.id,
      csvSafe(w.User?.name),
      csvSafe(w.receiverName),
      csvSafe(w.receiverMobileNumber),
      w.amount,
      w.status,
      csvSafe(w.outlet),
      w.User?.flagged ? "Yes" : "No",
      format(new Date(w.createdAt), "MMM dd, yyyy hh:mm a"),
      csvSafe(w.note),
      csvSafe(w.approvedNotes)
    ]);

    const csvContent = [
      headers.join(","),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(","))
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `withdrawals_${currentStatus}_${format(new Date(), "yyyy-MM-dd")}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setShowExportMenu(false);
  };

  const exportToPDF = () => {
    if (!withdrawals || withdrawals.length === 0) {
      alert("No data to export");
      return;
    }

    const statusLabel = currentStatus.charAt(0).toUpperCase() + currentStatus.slice(1);
    const dateRangeLabel = dateRange.startDate && dateRange.endDate 
      ? `${format(dateRange.startDate, "MMM dd, yyyy")} - ${format(dateRange.endDate, "MMM dd, yyyy")}`
      : "All Dates";

    const htmlContent = `
      <div style="font-family: Arial, sans-serif; padding: 20px;">
        <h1 style="text-align: center; color: #0E64D1; margin-bottom: 5px;">Withdrawal Requests Report</h1>
        <p style="text-align: center; color: #666; margin-bottom: 20px;">
          Status: ${statusLabel} | Date Range: ${dateRangeLabel}
        </p>
        <p style="text-align: center; color: #666; margin-bottom: 20px;">
          Generated on: ${format(new Date(), "MMMM dd, yyyy hh:mm a")}
        </p>
        
        <table style="width: 100%; border-collapse: collapse; font-size: 10px;">
          <thead>
            <tr style="background-color: #0E64D1; color: white;">
              <th style="padding: 8px; border: 1px solid #ddd; text-align: left;">Ref #</th>
              <th style="padding: 8px; border: 1px solid #ddd; text-align: left;">Request From</th>
              <th style="padding: 8px; border: 1px solid #ddd; text-align: left;">Receiver</th>
              <th style="padding: 8px; border: 1px solid #ddd; text-align: left;">Mobile</th>
              <th style="padding: 8px; border: 1px solid #ddd; text-align: right;">Amount</th>
              <th style="padding: 8px; border: 1px solid #ddd; text-align: center;">Status</th>
              <th style="padding: 8px; border: 1px solid #ddd; text-align: left;">Type</th>
              <th style="padding: 8px; border: 1px solid #ddd; text-align: center;">Flagged</th>
              <th style="padding: 8px; border: 1px solid #ddd; text-align: left;">Date</th>
            </tr>
          </thead>
          <tbody>
            ${withdrawals.map((w, index) => `
              <tr style="background-color: ${index % 2 === 0 ? '#f9f9f9' : '#ffffff'};">
                <td style="padding: 8px; border: 1px solid #ddd;">#${escapeHtml(w.id)}</td>
                <td style="padding: 8px; border: 1px solid #ddd;">${escapeHtml(w.User?.name || "-")}</td>
                <td style="padding: 8px; border: 1px solid #ddd;">${escapeHtml(w.receiverName || "-")}</td>
                <td style="padding: 8px; border: 1px solid #ddd;">${escapeHtml(w.receiverMobileNumber || "-")}</td>
                <td style="padding: 8px; border: 1px solid #ddd; text-align: right;">₱${formatAmount(w.amount)}</td>
                <td style="padding: 8px; border: 1px solid #ddd; text-align: center;">
                  <span style="padding: 2px 8px; border-radius: 12px; font-size: 9px; ${
                    w.status === 'pending' 
                      ? 'background-color: #FFE771; color: #733F12;' 
                      : w.status === 'approved' 
                      ? 'background-color: #85EFAC; color: #114C29;' 
                      : 'background-color: #FCA5A5; color: #7F1D1D;'
                  }">${escapeHtml(w.status.charAt(0).toUpperCase() + w.status.slice(1))}</span>
                </td>
                <td style="padding: 8px; border: 1px solid #ddd;">${escapeHtml(w.outlet || "-")}</td>
                <td style="padding: 8px; border: 1px solid #ddd; text-align: center;">${w.User?.flagged ? "Yes" : "No"}</td>
                <td style="padding: 8px; border: 1px solid #ddd;">${format(new Date(w.createdAt), "MMM dd, yyyy")}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
        
        <div style="margin-top: 20px; padding-top: 10px; border-top: 1px solid #ddd;">
          <p style="text-align: right; font-weight: bold; color: #333;">
            Total Records: ${withdrawals.length}
          </p>
          <p style="text-align: right; font-weight: bold; color: #0E64D1;">
            Total Amount: ₱${formatAmount(withdrawals.reduce((sum, w) => sum + Number(w.amount), 0))}
          </p>
        </div>
      </div>
    `;

    const container = document.createElement("div");
    container.innerHTML = htmlContent;
    document.body.appendChild(container);

    const opt = {
      margin: 10,
      filename: `withdrawals_${currentStatus}_${format(new Date(), "yyyy-MM-dd")}.pdf`,
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: { scale: 2 },
      jsPDF: { unit: "mm", format: "a4", orientation: "landscape" }
    };

    html2pdf().set(opt).from(container).save().then(() => {
      document.body.removeChild(container);
    });

    setShowExportMenu(false);
  };

  return (
    <div className="mb-4 space-y-2">
      {/* Search Input - Full Width on Mobile */}
      <form onSubmit={handleSearchSubmit} className="flex">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search by name, mobile, or amount..."
            value={localSearch}
            onChange={handleSearchChange}
            onKeyPress={handleKeyPress}
            className="w-full pl-10 pr-3 py-2.5 border border-gray-200 rounded-l-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
          />
        </div>
        <button
          type="submit"
          className="px-4 py-2.5 bg-[#0E64D1] text-white rounded-r-lg hover:bg-blue-700 transition-colors text-sm font-medium"
        >
          Search
        </button>
      </form>

      {/* Filter & Export Row - Side by Side */}
      <div className="flex gap-2">
        {/* Date Filter */}
        <div className="relative flex-1">
          <button
            onClick={() => setShowDatePicker(!showDatePicker)}
            className="flex items-center justify-between w-full gap-2 px-3 py-2.5 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-sm"
          >
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-gray-500" />
              {dateRange.startDate && dateRange.endDate ? (
                <span className="text-gray-700 text-xs">
                  {format(dateRange.startDate, "MM/dd")} - {format(dateRange.endDate, "MM/dd")}
                </span>
              ) : (
                <span className="text-gray-500">Filter by Date</span>
              )}
            </div>
            <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${showDatePicker ? 'rotate-180' : ''}`} />
          </button>

          {showDatePicker && (
            <>
              {/* Backdrop */}
              <div 
                className="fixed inset-0 z-40" 
                onClick={() => setShowDatePicker(false)}
              />
              <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 bg-white rounded-xl shadow-xl border border-gray-200 max-w-[95vw]">
                <div className="p-3 border-b flex items-center justify-between">
                  <span className="font-medium text-gray-800">Select Date Range</span>
                  <button onClick={() => setShowDatePicker(false)}>
                    <X className="h-5 w-5 text-gray-400" />
                  </button>
                </div>
                <DatePicker
                  selected={dateRange.startDate}
                  onChange={handleDateChange}
                  startDate={dateRange.startDate}
                  endDate={dateRange.endDate}
                  selectsRange
                  inline
                  maxDate={new Date()}
                />
                <div className="p-3 border-t flex gap-2">
                  <button
                    onClick={() => {
                      onDateRangeChange({ startDate: null, endDate: null });
                      setShowDatePicker(false);
                    }}
                    className="flex-1 px-3 py-2.5 text-sm text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium"
                  >
                    Clear
                  </button>
                  <button
                    onClick={() => setShowDatePicker(false)}
                    className="flex-1 px-3 py-2.5 text-sm bg-[#0E64D1] text-white rounded-lg hover:bg-blue-700 font-medium"
                  >
                    Done
                  </button>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Flagged Filter */}
        <button
          onClick={() => onFlaggedOnlyChange(!flaggedOnly)}
          title="Show only requests from flagged accounts"
          className={`flex items-center gap-2 px-4 py-2.5 rounded-lg transition-colors text-sm font-medium whitespace-nowrap border ${
            flaggedOnly
              ? "bg-red-600 border-red-600 text-white hover:bg-red-700"
              : "bg-white border-gray-200 text-gray-600 hover:bg-gray-50"
          }`}
        >
          <Flag className="h-4 w-4" />
          Flagged
        </button>

        {/* Export Button */}
        <div className="relative">
          <button
            onClick={() => setShowExportMenu(!showExportMenu)}
            className="flex items-center gap-2 px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium whitespace-nowrap"
          >
            <Download className="h-4 w-4" />
            Export
            <ChevronDown className={`h-4 w-4 transition-transform ${showExportMenu ? 'rotate-180' : ''}`} />
          </button>

          {showExportMenu && (
            <>
              {/* Backdrop */}
              <div 
                className="fixed inset-0 z-40" 
                onClick={() => setShowExportMenu(false)}
              />
              <div className="absolute right-0 top-full mt-2 z-50 bg-white rounded-lg shadow-xl border border-gray-200 min-w-[160px] overflow-hidden">
                <button
                  onClick={exportToPDF}
                  className="flex items-center gap-3 w-full px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 active:bg-gray-100 transition-colors"
                >
                  <FileText className="h-5 w-5 text-red-500" />
                  Export as PDF
                </button>
                <button
                  onClick={exportToCSV}
                  className="flex items-center gap-3 w-full px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 active:bg-gray-100 transition-colors border-t"
                >
                  <FileSpreadsheet className="h-5 w-5 text-green-500" />
                  Export as CSV
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Active Filters Display */}
      {hasActiveFilters && (
        <div className="flex flex-wrap items-center gap-2 pt-1">
          {searchQuery && (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-blue-50 text-blue-700 rounded-full text-xs">
              "{searchQuery}"
              <button
                onClick={() => {
                  setLocalSearch("");
                  onSearch("");
                }}
                className="hover:bg-blue-100 rounded-full p-0.5 ml-0.5"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          )}
          
          {dateRange.startDate && dateRange.endDate && (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-purple-50 text-purple-700 rounded-full text-xs">
              {format(dateRange.startDate, "MM/dd")} - {format(dateRange.endDate, "MM/dd/yy")}
              <button
                onClick={() => onDateRangeChange({ startDate: null, endDate: null })}
                className="hover:bg-purple-100 rounded-full p-0.5 ml-0.5"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          )}
          
          {flaggedOnly && (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-red-50 text-red-700 rounded-full text-xs">
              Flagged accounts only
              <button
                onClick={() => onFlaggedOnlyChange(false)}
                className="hover:bg-red-100 rounded-full p-0.5 ml-0.5"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          )}

          <button
            onClick={() => {
              setLocalSearch("");
              onClearFilters();
            }}
            className="text-xs text-red-600 hover:text-red-700 font-medium"
          >
            Clear all
          </button>
        </div>
      )}
    </div>
  );
}
